import _ from 'lodash';

import { HTTP_METHOD } from 'constants/http';
import { jsonStringify } from 'utils/json';

const DEFAULT_TIMEOUT = 1000;
const DEFAULT_ENABLE_LOGGING = true;
const DEFAULT_ENABLE_DEBUG_LOGGING = false;
const DEFAULT_EXCLUDE_URLS = [];
const DEFAULT_METHODS = [HTTP_METHOD.GET];
const MESSAGE_TAG = '[RequestDeduplicator]';

/**
 * RequestDeduplicator - class for preventing duplicate identical HTTP requests
 *
 * Features:
 * - Automatically removes requests from cache after timeout
 * - Supports exclusions for specific URLs
 * - Supports exclusions for specific methods
 *
 * Docs
 * check test cases
 */
class RequestDeduplicator {
  #pendingRequests = new Map();
  #requestTimeout;
  #enableLogging;
  #enableDebugLogging;
  #excludeUrls;
  #methods;
  #logger;

  /**
   * @param {Object} options - Configuration options
   * @param {number} options.timeout - Deduplication cache lifetime (ms)
   * @param {boolean} options.enableLogging - Enable logging
   * @param {boolean} options.enableDebugLogging - Enable debug logging (Added, Removed, Cleared)
   * @param {string[]} options.excludeUrls - URL patterns to exclude
   * @param {string[]} options.methods - HTTP methods to deduplicate
   * @param {Object} options.logger - Logger instance
   */
  constructor(options = {}) {
    this.#requestTimeout = options.timeout ?? DEFAULT_TIMEOUT;
    this.#enableLogging = options.enableLogging ?? DEFAULT_ENABLE_LOGGING;
    this.#enableDebugLogging = options.enableDebugLogging ?? DEFAULT_ENABLE_DEBUG_LOGGING;
    this.#excludeUrls = options.excludeUrls ?? DEFAULT_EXCLUDE_URLS;
    this.#methods = options.methods ?? DEFAULT_METHODS;
    this.#logger = options.logger ?? console;
  }

  #generateRequestKey(config) {
    const { method, url, params, data } = config;

    // Serialize config to string
    // Important: key order in object must be stable
    return jsonStringify({
      method: method?.toLowerCase(),
      url,
      params: this.#normalizeValue(params),
      data: this.#normalizeValue(data),
    });
  }

  #normalizeValue(value) {
    const isPlainObject = _.isPlainObject(value);

    if (_.isNil(value) || (isPlainObject && _.isEmpty(value))) {
      return;
    }

    if (_.isArray(value)) {
      return value.map(item => this.#normalizeValue(item));
    }

    if (isPlainObject) {
      return _(value)
        .toPairs()
        .sortBy(0)
        .fromPairs()
        .mapValues(v => this.#normalizeValue(v))
        .value();
    }

    return value;
  }

  shouldDeduplicate(config) {
    const method = config.method?.toLowerCase();

    if (!this.#methods.includes(method)) {
      return false;
    }

    if (this.#excludeUrls.some(pattern => config.url?.includes(pattern))) {
      return false;
    }

    if (config.skipDeduplication) {
      return false;
    }

    return true;
  }

  hasPendingRequest(config) {
    if (!this.shouldDeduplicate(config)) {
      return false;
    }

    const key = this.#generateRequestKey(config);

    return this.#pendingRequests.has(key);
  }

  addPendingRequest(config, promise) {
    if (!this.shouldDeduplicate(config)) {
      return null;
    }

    const key = this.#generateRequestKey(config);

    if (this.#pendingRequests.has(key)) {
      return key;
    }

    this.#pendingRequests.set(key, {
      promise,
      timestamp: Date.now(),
      config,
    });

    if (this.#enableLogging && this.#enableDebugLogging) {
      this.#logger.log(`${MESSAGE_TAG} Added: ${config.method?.toUpperCase()} ${config.url}`);
    }

    setTimeout(() => {
      this.removePendingRequest(key);
    }, this.#requestTimeout);

    return key;
  }

  getPendingRequest(config) {
    const key = this.#generateRequestKey(config);
    const entry = this.#pendingRequests.get(key);

    if (entry && this.#enableLogging) {
      const age = Date.now() - entry.timestamp;

      this.#logger.warn(
        `${MESSAGE_TAG} Duplicate detected (age: ${age}ms): ${config.method?.toUpperCase()} ${
          config.url
        }`,
      );
    }

    return entry?.promise;
  }

  getRequestKey(config) {
    return this.#generateRequestKey(config);
  }

  removePendingRequest(key) {
    if (this.#pendingRequests.has(key)) {
      if (this.#enableLogging && this.#enableDebugLogging) {
        const entry = this.#pendingRequests.get(key);
        const age = Date.now() - entry.timestamp;
        this.#logger.log(
          `${MESSAGE_TAG} Removed (age: ${age}ms): ${entry.config.method?.toUpperCase()} ${
            entry.config.url
          }`,
        );
      }

      this.#pendingRequests.delete(key);
    }
  }

  removePendingRequestByConfig(config) {
    const key = this.#generateRequestKey(config);
    this.removePendingRequest(key);
  }

  clearPendingRequests() {
    if (this.#enableLogging && this.#enableDebugLogging) {
      this.#logger.log(`${MESSAGE_TAG} Cleared ${this.#pendingRequests.size} pending requests`);
    }

    this.#pendingRequests.clear();
  }

  getPendingRequestsCount() {
    return this.#pendingRequests.size;
  }

  getStats() {
    const requests = Array.from(this.#pendingRequests.values());
    const now = Date.now();

    return {
      total: requests.length,
      byMethod: requests.reduce((acc, entry) => {
        const method = entry.config.method?.toUpperCase() || 'UNKNOWN';
        acc[method] = (acc[method] || 0) + 1;

        return acc;
      }, {}),
      oldestAge: requests.length ? Math.max(...requests.map(entry => now - entry.timestamp)) : 0,
    };
  }
}

export default RequestDeduplicator;
